import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm font-semibold font-subheading transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-[hsl(var(--button-primary))] focus-visible:ring-[hsl(var(--button-primary))] focus-visible:ring-opacity-40 focus-visible:ring-[3px] focus-visible:ring-offset-2 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'bg-selise-blue-500 text-[hsl(var(--white))] shadow-sm hover:bg-selise-blue-500/90 hover:shadow-md focus-visible:bg-selise-blue-500',
        destructive:
          'bg-destructive text-white shadow-sm hover:bg-destructive/90 hover:shadow-md focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background hover:bg-accent hover:text-accent-foreground hover:shadow-sm dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 hover:shadow-md px-4',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        primary:
          'bg-selise-blue-500 text-[hsl(var(--white))] shadow-sm hover:bg-selise-blue-500/90 hover:shadow-md focus-visible:bg-selise-blue-500',
        primaryOutline:
          'border border-selise-blue-500 bg-background text-selise-blue-500 shadow-sm hover:bg-accent hover:text-selise-blue-500/90 hover:shadow-md dark:bg-input/30',
      },
      size: {
        default: 'h-11 px-5 py-2.5 text-base has-[>svg]:px-4',
        sm: 'h-9 rounded-sm gap-1.5 px-3 text-sm has-[>svg]:px-2.5',
        // lg collapses to sm-sized below md so it fits comfortably in
        // mobile layouts; the full lg styling kicks in at md and up.
        lg:
          'h-9 rounded-sm gap-1.5 px-3 text-sm has-[>svg]:px-2.5 ' +
          'md:h-12 md:gap-2 md:px-7 md:text-lg md:has-[>svg]:px-5',
        icon: 'size-11',
        'icon-sm': 'size-9',
        // Same idea for icon buttons — sm size on mobile, lg on md+.
        'icon-lg': 'size-9 md:size-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  children,
  onClick,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  // Help the user select text in the button, need this for the uilm extension edge cases
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Only swallow the click when the selection is inside THIS button. `window.getSelection()`
    // is document-wide, so an unscoped check kills every button on the page whenever anything
    // else is selected — and Radix dialogs select their first input's text on open
    // (`focusFirst(..., { select: true })`), which left every button in a prefilled dialog dead.
    const selection = window.getSelection();
    const isSelectingThisButton =
      !!selection &&
      !selection.isCollapsed &&
      selection.toString().length > 0 &&
      !!selection.anchorNode &&
      e.currentTarget.contains(selection.anchorNode);

    if (isSelectingThisButton) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Otherwise, trigger the original onClick handler if it exists
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
      onClick={handleClick}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading && <LoaderCircle className="animate-spin" />}
          {children}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
